import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { useMemo } from 'react'

import type { HotelsApi } from '../api/hotelsApi'
import { HOTELS_LARGE_LIST_LIMIT } from '../constants/limits'
import type {
  BulkCreatePayload,
  BulkCreateResult,
  BulkDeleteResult,
  BulkExtensionHospitalityUpdateResult,
  CallHistoryEntry,
  ClearAllVoicemailsResult,
  CreateWakeupPayload,
  EmergencyCall,
  EnrichedRoom,
  EnrichedWakeupCall,
  ExtensionLine,
  ListRoomsParams,
  ListWakeupParams,
  PaginatedResult,
  PbxHospitalitySettings,
  ResetResult,
  Room,
  RoomResetSchedulePayload,
  RoomResetScheduleResult,
  Voicemail,
  WakeupCall,
} from '../types'
import type { ApiError } from '../types/api'
import { fetchAllPaginatedResults } from '../utils/paginatedFetch'
import { buildRoomExtensionMap, enrichRooms } from '../utils/roomEnrichment'
import { buildExtensionMap, enrichWakeupCalls } from '../utils/wakeupEnrichment'
import { HOTELS_QUERY_KEYS } from './queryKeys'

// ---------------------------------------------------------------------------
// Query Hooks
// ---------------------------------------------------------------------------

interface UseListRoomsParams {
  api: HotelsApi
  params: ListRoomsParams
  extensionLines?: ExtensionLine[]
  options?: Omit<
    UseQueryOptions<PaginatedResult<Room>, ApiError>,
    'queryKey' | 'queryFn'
  >
}

/** List hospitality rooms for a PBX, enriched with extension data client-side */
export const useListRooms = ({
  api,
  params,
  extensionLines,
  options,
}: UseListRoomsParams) => {
  const query = useQuery<PaginatedResult<Room>, ApiError>({
    queryKey: HOTELS_QUERY_KEYS.roomsList(params.pbx_id),
    queryFn: async () => {
      const response = await api.listRooms(params)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      const data = response.data as PaginatedResult<Room>
      const normalized = data.results.map((room) => ({
        ...room,
        extension_uuid: room.extension_uuid || room.uuid,
        status_code: (room.status_code ?? room.status) as Room['status_code'],
      }))

      const seen = new Set<string>()
      const deduped = normalized.filter((room) => {
        const key = room.extension_uuid ?? room.uuid
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
      })

      // Preserve the server's total `count` (used for pagination), adjusting
      // only for the duplicates removed from this page rather than overwriting
      // it with the current page's length.
      const removed = normalized.length - deduped.length
      const count = Math.max(0, (data.count ?? deduped.length) - removed)

      return { ...data, count, results: deduped }
    },
    enabled: params.pbx_id > 0 && !!params.pbx_uuid,
    ...options,
    retry: options?.retry ?? false,
  })

  const extMap = useMemo(
    () => (extensionLines ? buildRoomExtensionMap(extensionLines) : undefined),
    [extensionLines],
  )

  const enrichedData = useMemo(():
    PaginatedResult<EnrichedRoom> | undefined => {
    if (!query.data) return undefined
    if (!extMap) return query.data as unknown as PaginatedResult<EnrichedRoom>
    return {
      ...query.data,
      results: enrichRooms(query.data.results, extMap),
    }
  }, [query.data, extMap])

  return {
    ...query,
    data: enrichedData,
  }
}

interface UseGetRoomParams {
  api: HotelsApi
  /** Extension UUID — hotels-service keys rooms by their extension UUID. */
  extensionUuid: string
  options?: Omit<UseQueryOptions<Room, ApiError>, 'queryKey' | 'queryFn'>
}

/** Get a single room by extension UUID */
export const useGetRoom = ({
  api,
  extensionUuid,
  options,
}: UseGetRoomParams) => {
  return useQuery<Room, ApiError>({
    queryKey: HOTELS_QUERY_KEYS.roomDetail(extensionUuid),
    queryFn: async () => {
      const response = await api.getRoom(extensionUuid)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as Room
    },
    enabled: !!extensionUuid,
    ...options,
    retry: options?.retry ?? false,
  })
}

interface UseListWakeupCallsParams {
  api: HotelsApi
  params: ListWakeupParams
  extensionLines?: ExtensionLine[]
  options?: Omit<
    UseQueryOptions<PaginatedResult<WakeupCall>, ApiError>,
    'queryKey' | 'queryFn'
  >
}

/** List wake-up calls for a PBX (filtered by UUID) */
export const useListWakeupCalls = ({
  api,
  params,
  extensionLines,
  options,
}: UseListWakeupCallsParams) => {
  const query = useQuery<PaginatedResult<WakeupCall>, ApiError>({
    queryKey: HOTELS_QUERY_KEYS.wakeupCallsList(params.pbx_id, {
      include_deleted: params.include_deleted,
      extension_id: params.extension_id,
      limit: params.limit,
      offset: params.offset,
    }),
    queryFn: async () => {
      const response = await api.listWakeupCalls(params)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as PaginatedResult<WakeupCall>
    },
    enabled: !!params.pbx_id,
    ...options,
    retry: options?.retry ?? false,
  })

  const extMap = useMemo(
    () => (extensionLines ? buildExtensionMap(extensionLines) : undefined),
    [extensionLines],
  )

  const enrichedData = useMemo(() => {
    if (!query.data || !extMap) return query.data
    return {
      ...query.data,
      results: enrichWakeupCalls(query.data.results, extMap),
    } as PaginatedResult<EnrichedWakeupCall>
  }, [query.data, extMap])

  return { ...query, data: enrichedData }
}

interface UseListWakeupCallsAllParams {
  api: HotelsApi
  params: Omit<ListWakeupParams, 'offset'>
  extensionLines?: ExtensionLine[]
  options?: Omit<
    UseQueryOptions<PaginatedResult<WakeupCall>, ApiError>,
    'queryKey' | 'queryFn'
  >
}

/**
 * List all wake-up calls for a PBX by fetching every paginated page.
 * Useful for history views that need full local filtering/pagination.
 */
export const useListWakeupCallsAll = ({
  api,
  params,
  extensionLines,
  options,
}: UseListWakeupCallsAllParams) => {
  const pageSize = params.limit ?? HOTELS_LARGE_LIST_LIMIT

  const query = useQuery<PaginatedResult<WakeupCall>, ApiError>({
    queryKey: HOTELS_QUERY_KEYS.wakeupCallsList(params.pbx_id, {
      include_deleted: params.include_deleted,
      extension_id: params.extension_id,
      limit: pageSize,
      all_pages: true,
    }),
    queryFn: async () => {
      return fetchAllPaginatedResults<WakeupCall, ApiError>({
        initialLimit: pageSize,
        fetchPage: ({ limit, offset }) =>
          api.listWakeupCalls({
            ...params,
            limit,
            offset,
          }),
      })
    },
    enabled: !!params.pbx_id,
    ...options,
    retry: options?.retry ?? false,
  })

  const extMap = useMemo(
    () => (extensionLines ? buildExtensionMap(extensionLines) : undefined),
    [extensionLines],
  )

  const enrichedData = useMemo(() => {
    if (!query.data || !extMap) return query.data
    return {
      ...query.data,
      results: enrichWakeupCalls(query.data.results, extMap),
    } as PaginatedResult<EnrichedWakeupCall>
  }, [query.data, extMap])

  return { ...query, data: enrichedData }
}

interface UseListCallHistoryParams {
  api: HotelsApi
  pbxId: number
  options?: Omit<
    UseQueryOptions<CallHistoryEntry[], ApiError>,
    'queryKey' | 'queryFn'
  >
}

/** List incoming call history for a PBX (max 100, paginated response unwrapped) */
export const useListCallHistory = ({
  api,
  pbxId,
  options,
}: UseListCallHistoryParams) => {
  return useQuery<CallHistoryEntry[], ApiError>({
    queryKey: HOTELS_QUERY_KEYS.callHistoryList(pbxId),
    queryFn: async () => {
      const response = await api.listCallHistory(pbxId)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      const data = response.data as PaginatedResult<CallHistoryEntry>
      return data.results
    },
    enabled: pbxId > 0,
    ...options,
    retry: options?.retry ?? false,
  })
}

interface UseListEmergencyCallsParams {
  api: HotelsApi
  pbxId: number
  options?: Omit<
    UseQueryOptions<EmergencyCall[], ApiError>,
    'queryKey' | 'queryFn'
  >
}

/** List emergency calls for a PBX (last 7 days, max 10) */
export const useListEmergencyCalls = ({
  api,
  pbxId,
  options,
}: UseListEmergencyCallsParams) => {
  return useQuery<EmergencyCall[], ApiError>({
    queryKey: HOTELS_QUERY_KEYS.emergencyCalls(pbxId),
    queryFn: async () => {
      const response = await api.listEmergencyCalls(pbxId)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      // Handle both flat array and paginated response shapes
      const data = response.data
      if (Array.isArray(data)) return data
      if (data && typeof data === 'object' && 'results' in data) {
        return (data as PaginatedResult<EmergencyCall>).results
      }
      return []
    },
    enabled: pbxId > 0,
    ...options,
    retry: options?.retry ?? false,
  })
}

interface UseListVoicemailsParams {
  api: HotelsApi
  roomId: number
  pbxId: number
  limit?: number
  offset?: number
  ordering?: string
  unread?: boolean
  options?: Omit<
    UseQueryOptions<PaginatedResult<Voicemail>, ApiError>,
    'queryKey' | 'queryFn'
  >
}

/** List voicemails for a hospitality room */
export const useListVoicemails = ({
  api,
  roomId,
  pbxId,
  limit,
  offset,
  ordering,
  unread,
  options,
}: UseListVoicemailsParams) => {
  return useQuery<PaginatedResult<Voicemail>, ApiError>({
    queryKey: HOTELS_QUERY_KEYS.voicemailsList(pbxId, roomId),
    queryFn: async () => {
      const response = await api.listVoicemails({
        room_id: roomId,
        pbx_id: pbxId,
        limit,
        offset,
        ordering,
        unread,
      })
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as PaginatedResult<Voicemail>
    },
    enabled: roomId > 0 && pbxId > 0,
    ...options,
    retry: options?.retry ?? false,
  })
}

interface UseGetPbxParams {
  api: HotelsApi
  /** PBX UUID — hotels-service keys the PBX by UUID. */
  pbxUuid: string
  /** Numeric PBX id, used for the cache key (and by admin3's app v2 override). */
  pbxId: number
  options?: Omit<
    UseQueryOptions<PbxHospitalitySettings, ApiError>,
    'queryKey' | 'queryFn'
  >
}

/** Get PBX hospitality settings (industry_type, xml_rebuilt_at) */
export const useGetPbx = ({
  api,
  pbxUuid,
  pbxId,
  options,
}: UseGetPbxParams) => {
  return useQuery<PbxHospitalitySettings, ApiError>({
    queryKey: HOTELS_QUERY_KEYS.pbx(pbxId),
    queryFn: async () => {
      const response = await api.getPbx(pbxUuid, pbxId)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as PbxHospitalitySettings
    },
    enabled: pbxId > 0,
    ...options,
    retry: options?.retry ?? false,
  })
}

// ---------------------------------------------------------------------------
// Mutation Hooks
// ---------------------------------------------------------------------------

interface UseUpdateRoomStatusParams {
  api: HotelsApi
  pbxId: number
  options?: Omit<
    UseMutationOptions<
      Room,
      ApiError,
      { extensionUuid: string; status: number }
    >,
    'mutationFn'
  >
}

/**
 * Update a room's hospitality status.
 * Invalidates room list and detail caches on success.
 * Note: CLEAN/INSPECTED statuses trigger server-side cleanup of
 * wake-up calls and voicemails for the room.
 */
export const useUpdateRoomStatus = ({
  api,
  pbxId,
  options,
}: UseUpdateRoomStatusParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<Room, ApiError, { extensionUuid: string; status: number }>(
    {
      mutationFn: async ({ extensionUuid, status }) => {
        const response = await api.updateRoomStatus(extensionUuid, status)
        if (!response.ok) {
          return Promise.reject(response.data as ApiError)
        }
        return response.data as Room
      },
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.setQueryData<PaginatedResult<Room>>(
          HOTELS_QUERY_KEYS.roomsList(pbxId),
          (previous) => {
            if (!previous) return previous
            return {
              ...previous,
              results: previous.results.map((room) =>
                room.extension_uuid === variables.extensionUuid
                  ? {
                      ...room,
                      ...data,
                      status_code: variables.status as Room['status_code'],
                    }
                  : room,
              ),
            }
          },
        )
        queryClient.setQueryData<Room>(
          HOTELS_QUERY_KEYS.roomDetail(variables.extensionUuid),
          (prev) =>
            prev
              ? {
                  ...prev,
                  ...data,
                  status_code: variables.status as Room['status_code'],
                }
              : undefined,
        )

        // Invalidate room list for this PBX
        queryClient.invalidateQueries({
          queryKey: HOTELS_QUERY_KEYS.roomsList(pbxId),
          refetchType: 'none',
        })
        // Invalidate the specific room detail
        queryClient.invalidateQueries({
          queryKey: HOTELS_QUERY_KEYS.roomDetail(variables.extensionUuid),
          refetchType: 'none',
        })
        // CLEAN/INSPECTED statuses clean up wakeups and voicemails server-side
        queryClient.invalidateQueries({
          queryKey: HOTELS_QUERY_KEYS.wakeupCalls(),
        })
        queryClient.invalidateQueries({
          queryKey: HOTELS_QUERY_KEYS.voicemails(),
        })
        onSuccessOption?.(data, variables, onMutateResult, context)
      },
      ...restOptions,
    },
  )
}

interface UseResetAllRoomsParams {
  api: HotelsApi
  options?: Omit<
    UseMutationOptions<
      ResetResult,
      ApiError,
      { pbxUuid: string; pbxId: number }
    >,
    'mutationFn'
  >
}

/** Reset all rooms to DIRTY status for a PBX */
export const useResetAllRooms = ({ api, options }: UseResetAllRoomsParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<ResetResult, ApiError, { pbxUuid: string; pbxId: number }>(
    {
      mutationFn: async ({ pbxUuid }) => {
        const response = await api.resetAllRooms(pbxUuid)
        if (!response.ok) {
          return Promise.reject(response.data as ApiError)
        }
        return response.data as ResetResult
      },
      onSuccess: (data, variables, onMutateResult, context) => {
        queryClient.invalidateQueries({
          queryKey: HOTELS_QUERY_KEYS.roomsList(variables.pbxId),
        })
        onSuccessOption?.(data, variables, onMutateResult, context)
      },
      ...restOptions,
    },
  )
}

interface UseCreateWakeupCallParams {
  api: HotelsApi
  options?: Omit<
    UseMutationOptions<WakeupCall, ApiError, CreateWakeupPayload>,
    'mutationFn'
  >
}

/** Create a single wake-up call */
export const useCreateWakeupCall = ({
  api,
  options,
}: UseCreateWakeupCallParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<WakeupCall, ApiError, CreateWakeupPayload>({
    mutationFn: async (data) => {
      const response = await api.createWakeupCall(data)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as WakeupCall
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.wakeupCalls(),
      })
      // Room overview counts include wakeup count
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.rooms(),
      })
      onSuccessOption?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

interface UseBulkCreateWakeupCallsParams {
  api: HotelsApi
  options?: Omit<
    UseMutationOptions<BulkCreateResult, ApiError, BulkCreatePayload>,
    'mutationFn'
  >
}

/** Bulk create wake-up calls for multiple rooms */
export const useBulkCreateWakeupCalls = ({
  api,
  options,
}: UseBulkCreateWakeupCallsParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<BulkCreateResult, ApiError, BulkCreatePayload>({
    mutationFn: async (data) => {
      const response = await api.bulkCreateWakeupCalls(data)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as BulkCreateResult
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.wakeupCalls(),
      })
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.rooms(),
      })
      onSuccessOption?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

interface UseDeleteWakeupCallParams {
  api: HotelsApi
  options?: Omit<
    UseMutationOptions<void, ApiError, { id: number | string }>,
    'mutationFn'
  >
}

/** Delete (soft-cancel) a single wake-up call */
export const useDeleteWakeupCall = ({
  api,
  options,
}: UseDeleteWakeupCallParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<void, ApiError, { id: number | string }>({
    mutationFn: async ({ id }) => {
      const response = await api.deleteWakeupCall(id)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.wakeupCalls(),
      })
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.rooms(),
      })
      onSuccessOption?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

interface UseBulkDeleteWakeupCallsParams {
  api: HotelsApi
  options?: Omit<
    UseMutationOptions<
      BulkDeleteResult,
      ApiError,
      { extensionUuid: string; pbxUuid: string }
    >,
    'mutationFn'
  >
}

/** Bulk cancel all wake-up calls for an extension */
export const useBulkDeleteWakeupCalls = ({
  api,
  options,
}: UseBulkDeleteWakeupCallsParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<
    BulkDeleteResult,
    ApiError,
    { extensionUuid: string; pbxUuid: string }
  >({
    mutationFn: async ({ extensionUuid, pbxUuid }) => {
      const response = await api.bulkDeleteWakeupCalls(extensionUuid, pbxUuid)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as BulkDeleteResult
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.wakeupCalls(),
      })
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.rooms(),
      })
      onSuccessOption?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

interface UseDeleteVoicemailParams {
  api: HotelsApi
  options?: Omit<
    UseMutationOptions<void, ApiError, { id: number }>,
    'mutationFn'
  >
}

/** Delete (soft-delete) a single voicemail */
export const useDeleteVoicemail = ({
  api,
  options,
}: UseDeleteVoicemailParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<void, ApiError, { id: number }>({
    mutationFn: async ({ id }) => {
      const response = await api.deleteVoicemail(id)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.voicemails(),
      })
      // Room overview counts include voicemail count
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.rooms(),
      })
      onSuccessOption?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

interface UseClearAllVoicemailsParams {
  api: HotelsApi
  options?: Omit<
    UseMutationOptions<
      ClearAllVoicemailsResult,
      ApiError,
      { roomId: number; pbxId: number }
    >,
    'mutationFn'
  >
}

/** Clear all voicemails for a hospitality room */
export const useClearAllVoicemails = ({
  api,
  options,
}: UseClearAllVoicemailsParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<
    ClearAllVoicemailsResult,
    ApiError,
    { roomId: number; pbxId: number }
  >({
    mutationFn: async ({ roomId, pbxId }) => {
      const response = await api.clearAllVoicemails(roomId, pbxId)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as ClearAllVoicemailsResult
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.voicemailsList(
          variables.pbxId,
          variables.roomId,
        ),
      })
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.voicemails(),
      })
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.rooms(),
      })
      onSuccessOption?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

// ---------------------------------------------------------------------------
// Settings Hooks (Extension Lines + System Settings)
// ---------------------------------------------------------------------------

interface UseListExtensionLinesParams {
  api: HotelsApi
  pbxId: number
  options?: Omit<
    UseQueryOptions<ExtensionLine[], ApiError>,
    'queryKey' | 'queryFn'
  >
}

/** List all extension lines for a PBX (for Settings activation table) */
export const useListExtensionLines = ({
  api,
  pbxId,
  options,
}: UseListExtensionLinesParams) => {
  return useQuery<ExtensionLine[], ApiError>({
    queryKey: HOTELS_QUERY_KEYS.extensionLinesList(pbxId),
    queryFn: async () => {
      const response = await api.listExtensionLines(pbxId)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as ExtensionLine[]
    },
    enabled: pbxId > 0,
    ...options,
    retry: options?.retry ?? false,
  })
}

interface UseToggleExtensionHospitalityParams {
  api: HotelsApi
  pbxId: number
  options?: Omit<
    UseMutationOptions<
      ExtensionLine,
      ApiError,
      { extensionId: number; hospitality: boolean },
      ToggleExtensionHospitalityMutateContext
    >,
    'mutationFn'
  >
}

interface ToggleExtensionHospitalityMutateContext {
  previousExtensions?: ExtensionLine[]
}

/** Toggle hospitality on/off for an extension */
export const useToggleExtensionHospitality = ({
  api,
  pbxId,
  options,
}: UseToggleExtensionHospitalityParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}
  const extensionLinesKey = HOTELS_QUERY_KEYS.extensionLinesList(pbxId)

  return useMutation<
    ExtensionLine,
    ApiError,
    { extensionId: number; hospitality: boolean },
    ToggleExtensionHospitalityMutateContext
  >({
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: extensionLinesKey,
      })

      const previousExtensions =
        queryClient.getQueryData<ExtensionLine[]>(extensionLinesKey)

      queryClient.setQueryData<ExtensionLine[]>(
        extensionLinesKey,
        (current) => {
          if (!current) return current
          return current.map((ext) =>
            ext.id === variables.extensionId
              ? { ...ext, hospitality: variables.hospitality }
              : ext,
          )
        },
      )

      return { previousExtensions }
    },
    mutationFn: async ({ extensionId, hospitality }) => {
      const response = await api.toggleExtensionHospitality(
        extensionId,
        hospitality,
      )
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as ExtensionLine
    },
    onError: (_error, _variables, onMutateResult) => {
      if (!onMutateResult?.previousExtensions) return

      queryClient.setQueryData(
        extensionLinesKey,
        onMutateResult.previousExtensions,
      )
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<ExtensionLine[]>(
        extensionLinesKey,
        (current) => {
          if (!current) return current
          return current.map((ext) =>
            ext.id === variables.extensionId
              ? {
                  ...ext,
                  ...data,
                  hospitality: !!data.hospitality,
                }
              : ext,
          )
        },
      )

      queryClient.invalidateQueries({
        queryKey: extensionLinesKey,
      })
      // Room list changes when hospitality toggles
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.rooms(),
      })
      onSuccessOption?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

interface UseBulkUpdateExtensionHospitalityParams {
  api: HotelsApi
  pbxId: number
  options?: Omit<
    UseMutationOptions<
      BulkExtensionHospitalityUpdateResult,
      ApiError,
      { extensionIds: number[]; hospitality: boolean }
    >,
    'mutationFn'
  >
}

/** Bulk update hospitality on/off for multiple extensions */
export const useBulkUpdateExtensionHospitality = ({
  api,
  pbxId,
  options,
}: UseBulkUpdateExtensionHospitalityParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<
    BulkExtensionHospitalityUpdateResult,
    ApiError,
    { extensionIds: number[]; hospitality: boolean }
  >({
    mutationFn: async ({ extensionIds, hospitality }) => {
      const response = await api.bulkUpdateExtensionHospitality(
        extensionIds,
        hospitality,
      )
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as BulkExtensionHospitalityUpdateResult
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.extensionLinesList(pbxId),
      })
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.rooms(),
      })
      onSuccessOption?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

interface UseSetRoomResetScheduleParams {
  api: HotelsApi
  options?: Omit<
    UseMutationOptions<
      RoomResetScheduleResult,
      ApiError,
      RoomResetSchedulePayload
    >,
    'mutationFn'
  >
}

/**
 * Set the daily room reset schedule on a PBX. On success, invalidates
 * `HOTELS_QUERY_KEYS.pbx(pbx_id)` so the form reflects the persisted
 * value when the user revisits the page.
 */
export const useSetRoomResetSchedule = ({
  api,
  options,
}: UseSetRoomResetScheduleParams) => {
  const queryClient = useQueryClient()
  const { onSuccess: onSuccessOption, ...restOptions } = options ?? {}

  return useMutation<
    RoomResetScheduleResult,
    ApiError,
    RoomResetSchedulePayload
  >({
    mutationFn: async (data) => {
      const response = await api.setRoomResetSchedule(data)
      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
      return response.data as RoomResetScheduleResult
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: HOTELS_QUERY_KEYS.pbx(variables.pbx_id),
      })
      onSuccessOption?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}
